import { useAuth } from '../../lib/auth';
import type { VisitOutcome } from '../../types';

export const OUTCOME_LABEL: Record<VisitOutcome, string> = {
  order_taken: 'Order taken',
  no_order: 'No order',
  shop_closed: 'Shop closed',
  owner_absent: 'Owner not available',
  job_done: 'Job completed',
  job_pending: 'Could not finish',
  follow_up: 'Follow-up agreed',
  other: 'Something else',
};

/**
 * What each flag means, in the words the admin would use.
 *
 * A raw flag like FAR_FROM_CUSTOMER reads as an accusation. Most of
 * these are not: a wrong pin in the master data produces the same flag
 * as a rep filling in visits from a café, and the console should not
 * pretend to know which it is looking at.
 */
export const FLAG_LABEL: Record<string, { label: string; tone: 'warning' | 'danger' | 'neutral'; why: string }> = {
  FAR_FROM_CUSTOMER: {
    label: 'Far from the shop',
    tone: 'warning',
    why: 'Recorded away from the shop\'s stored pin. Often the pin is wrong — '
       + 'open the visit to see the distance and fix the pin if so.',
  },
  CUSTOMER_HAS_NO_PIN: {
    label: 'Shop has no pin',
    tone: 'neutral',
    why: 'Nothing to compare the location against. Capture the shop\'s location '
       + 'on the next visit.',
  },
  NO_LOCATION: {
    label: 'No location',
    tone: 'warning',
    why: 'The phone gave no position — usually location switched off, sometimes '
       + 'no GPS fix indoors.',
  },
  MOCK_LOCATION: {
    label: 'Fake location app',
    tone: 'danger',
    why: 'The phone reported that its position came from a mock-location app.',
  },
  OFF_PLAN: {
    label: 'Off plan',
    tone: 'neutral',
    why: 'Not on this rep\'s journey plan for the day. Off-plan calls are '
       + 'allowed — a rep who passes a shop that needs stock should call on it.',
  },
  VERY_SHORT_VISIT: {
    label: 'Very short',
    tone: 'warning',
    why: 'Shorter than the minimum in Settings. A rep can be turned away at the '
       + 'door in thirty seconds, so this is worth a look rather than a verdict.',
  },
  VERY_LONG_VISIT: {
    label: 'Never closed',
    tone: 'warning',
    why: 'Ran far longer than a visit should — usually the rep forgot to check out.',
  },
  FACE_LOW_CONFIDENCE: {
    label: 'Face match unsure',
    tone: 'warning',
    why: 'The selfie matched, but not confidently. Poor light does this.',
  },
  FACE_CHECK_UNAVAILABLE: {
    label: 'Face not checked',
    tone: 'neutral',
    why: 'The photo could not be checked — a corrupt image, or the check was '
       + 'unavailable. The visit itself was kept.',
  },
  NOT_ENROLLED: {
    label: 'No face on file',
    tone: 'neutral',
    why: 'This rep has not set up face check-in, so the selfie could not be matched.',
  },
  MULTIPLE_FACES: {
    label: 'More than one face',
    tone: 'warning',
    why: 'Another person was in the frame.',
  },
  DEVICE_ONLY_FACE_CHECK: {
    label: 'Checked on the phone',
    tone: 'neutral',
    why: 'Matched on the device rather than the server. Faster, and fine in '
       + 'normal use, but a modified phone could fake it.',
  },
  SIGNATURE_SKIPPED: {
    label: 'Not signed',
    tone: 'neutral',
    why: 'Nobody was available to sign. The reason the rep gave is on the visit.',
  },
  ABANDONED: {
    label: 'Abandoned',
    tone: 'neutral',
    why: 'The rep gave up on this visit rather than recording an outcome.',
  },
};

export function flagInfo(flag: string) {
  return FLAG_LABEL[flag] ?? {
    label: flag.toLowerCase().replace(/_/g, ' '),
    tone: 'neutral' as const,
    why: '',
  };
}

export function duration(seconds: number | null | undefined): string {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

/**
 * A visit happened at a wall-clock time in the company's timezone, and
 * that is the only reading of it anybody wants: a sales manager on a
 * call from London must not see the Mumbai team's day shift by five and
 * a half hours. So the timezone comes from the company, never from the
 * browser.
 */
export function clockTime(iso: string | null | undefined, timeZone?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString([], {
    hour: '2-digit', minute: '2-digit', timeZone: timeZone || undefined,
  });
}

/** Today where the company is, for the same reason. */
export function today(timeZone?: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: timeZone || undefined })
    .format(new Date());
}

export function daysAgo(n: number, timeZone?: string): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return new Intl.DateTimeFormat('en-CA', { timeZone: timeZone || undefined }).format(d);
}

/**
 * The company's timezone, for every date and time on these screens.
 *
 * A hook rather than a prop threaded through six components: every
 * screen here needs it, and the one thing worse than passing it
 * everywhere is a component that quietly forgets and falls back to the
 * viewer's own clock.
 */
export function useCompanyTimeZone(): string | undefined {
  return useAuth().company?.timezone || undefined;
}
